import { getBaseUrl } from './baseUrl';

export type AuthUser = {
  id: string;
  username: string;
  email: string | null;
  fullName: string | null;
  isActive: boolean;
  role: string | null;
  roleLabel: string | null;
  scopeType?: 'UNIT' | 'GROUP';
  scopeOrgGroupId?: string | null;
  organizationId: string;
  organizationSlug: string | null;
  organizationName: string | null;
  organizationGroupId?: string | null;
};

export type AuthSession = {
  token: string;
  tokenType: string;
  user: AuthUser;
  permissions: string[];
  knowledgeGraphAccess?: {
    accessLevel: 'FULL' | 'UPLOAD_ONLY' | 'EDIT_ONLY' | 'READ_ONLY';
    canRead: boolean;
    canUpload: boolean;
    canEdit: boolean;
    canFeedback: boolean;
  };
  customerPortalAccess?: {
    mode: 'FULL' | 'LITE';
    isLite: boolean;
    customers?: Array<{
      id: string;
      company?: string | null;
      companyName?: string | null;
      email?: string | null;
      phone?: string | null;
      assetNumbersEnabled?: boolean;
      isActive?: boolean;
    }>;
  };
};

const SESSION_STORAGE_KEY = 'fieldservice.auth.session';
const LOGIN_NOTICE_STORAGE_KEY = 'fieldservice.auth.loginNotice';
export const AUTH_SESSION_EXPIRED_EVENT = 'fieldservice:auth-session-expired';
export const API_ERROR_DIAGNOSTIC_EVENT = 'bonzaiq:api-error';
const SESSION_EXPIRED_NOTICE = 'Your session expired. Sign in again to continue.';
const LICENCE_ACCESS_NOTICE = 'Licence access is unavailable for this account. Contact your administrator or software support.';

let authToken: string | null = null;
let fetchInterceptorInstalled = false;
let sessionExpiryHandled = false;

function parseSession(raw: string | null): AuthSession | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed?.token || !parsed?.user?.id) return null;
    return {
      ...parsed,
      permissions: Array.isArray(parsed.permissions) ? parsed.permissions : []
    };
  } catch {
    return null;
  }
}

export function loadStoredSession(): AuthSession | null {
  if (typeof window === 'undefined') return null;
  return parseSession(window.localStorage.getItem(SESSION_STORAGE_KEY));
}

export function saveSession(session: AuthSession): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  authToken = session.token;
  sessionExpiryHandled = false;
}

export function clearSession(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  }
  authToken = null;
}

export function storeLoginNotice(message: string): void {
  if (typeof window === 'undefined') return;
  const trimmed = String(message || '').trim();
  if (!trimmed) return;
  window.localStorage.setItem(LOGIN_NOTICE_STORAGE_KEY, trimmed);
}

export function consumeLoginNotice(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(LOGIN_NOTICE_STORAGE_KEY);
  if (!raw) return null;
  window.localStorage.removeItem(LOGIN_NOTICE_STORAGE_KEY);
  const trimmed = raw.trim();
  return trimmed || null;
}

export function getAuthToken(): string | null {
  if (authToken) return authToken;
  const stored = loadStoredSession();
  authToken = stored?.token ?? null;
  return authToken;
}

function isAuthSessionBoundaryRequest(url: string): boolean {
  return /\/api\/auth\/(?:login|bootstrap)\b/i.test(url) || url.includes('/api/platform-support/');
}

function handleExpiredSession(sourceUrl: string, message = SESSION_EXPIRED_NOTICE): void {
  if (typeof window === 'undefined' || sessionExpiryHandled) return;
  sessionExpiryHandled = true;
  clearSession();
  storeLoginNotice(message);
  window.dispatchEvent(
    new CustomEvent(AUTH_SESSION_EXPIRED_EVENT, {
      detail: {
        message,
        sourceUrl
      }
    })
  );
  if (!String(window.location.hash || '').startsWith('#/login')) {
    window.location.hash = '/login';
  }
}

export function installAuthFetchInterceptor(): void {
  if (fetchInterceptorInstalled || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }
  fetchInterceptorInstalled = true;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = getAuthToken();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

    const isApiRequest = url.includes('/api/');
    if (!isApiRequest) return nativeFetch(input, init);

    const existingHeaders =
      init?.headers || (input instanceof Request ? input.headers : undefined);
    const headers = new Headers(existingHeaders);
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const method = String(
      init?.method || (input instanceof Request ? input.method : 'GET')
    ).toUpperCase();
    const requestInit: RequestInit = {
      ...init,
      headers,
      ...(token && method === 'GET' ? { cache: 'no-store' as RequestCache } : {})
    };

    const requestPromise =
      input instanceof Request
        ? nativeFetch(new Request(input, requestInit))
        : nativeFetch(input, requestInit);

    return requestPromise.then(response => {
      const licenceAccessDenied =
        response.status === 403 &&
        String(response.headers.get('X-Auth-Access-Denied') || '').toLowerCase() === 'licence';
      if ((response.status === 401 || licenceAccessDenied) && token && !isAuthSessionBoundaryRequest(url)) {
        handleExpiredSession(url, licenceAccessDenied ? LICENCE_ACCESS_NOTICE : SESSION_EXPIRED_NOTICE);
      }
      if (!response.ok) {
        let safePath = '/api/request';
        try { safePath = new URL(url, window.location.origin).pathname; } catch { /* keep safe fallback */ }
        window.dispatchEvent(new CustomEvent(API_ERROR_DIAGNOSTIC_EVENT, { detail: { message: `${method} ${safePath} returned ${response.status}` } }));
      }
      return response;
    }).catch(error => {
      window.dispatchEvent(new CustomEvent(API_ERROR_DIAGNOSTIC_EVENT, { detail: { message: `${method} API request failed: ${error instanceof Error ? error.message : 'Network error'}` } }));
      throw error;
    });
  };
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => null)) as T | { error?: string } | null;
  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload && payload.error
        ? String(payload.error)
        : 'Request failed';
    if (process.env.NODE_ENV === 'development') {
      throw new Error(`${message} (status ${response.status} @ ${response.url})`);
    }
    throw new Error(message);
  }
  return payload as T;
}

export async function loginWithPassword(input: {
  username: string;
  password: string;
  organizationSlug: string;
}): Promise<AuthSession> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return parseJsonResponse<AuthSession>(response);
}

export async function createStandaloneUnitAdmin(input: {
  organizationName: string;
  organizationSlug: string;
  username: string;
  password: string;
  fullName?: string;
  email?: string;
  licenseActivationKey: string;
}): Promise<AuthSession> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/auth/bootstrap`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return parseJsonResponse<AuthSession>(response);
}

export async function fetchCurrentSession(token: string): Promise<{
  user: AuthUser;
  permissions: string[];
  knowledgeGraphAccess?: AuthSession['knowledgeGraphAccess'];
  customerPortalAccess?: AuthSession['customerPortalAccess'];
}> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/auth/me`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return parseJsonResponse<{
    user: AuthUser;
    permissions: string[];
    knowledgeGraphAccess?: AuthSession['knowledgeGraphAccess'];
    customerPortalAccess?: AuthSession['customerPortalAccess'];
  }>(response);
}

export async function logoutProductSession(token: string): Promise<void> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/auth/logout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    keepalive: true
  });
  if (!response.ok) await parseJsonResponse(response);
}

export async function changeOwnPassword(input: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ ok: true }> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return parseJsonResponse<{ ok: true }>(response);
}

export async function deactivateOwnPortalAccess(): Promise<{ ok: true }> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/auth/deactivate-account`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  return parseJsonResponse<{ ok: true }>(response);
}
